import React, { useState } from 'react';
import { Card, CardHeader, Grid, Tooltip } from '@mui/material';
import {
  ScenarioCardContainer,
  StyledCardContent,
  StyledCardHeaderBox,
  StyledCardHeaderTitle,
  StyledStatusText,
  StyledGridContainer,
  StyledGridLeft,
  StyledGridRight,
  StyledUserName,
  StyledUserDate,
  StyledImage,
  StyledMetricTypography,
  StyledMetricBox,
  StyledMetricGrid,
  StyledNoBorderGrid,
  StyledHeaderContainer,
  StyledTagImage,
  StyledMetricTitle,
  StyledCheckbox,
  CompareText,
} from './ScenarioCards.Styled';
import {
  Editable,
  Shareable,
  NonShareable,
  Update,
  NoUpdate,
  Delete,
  UserIconLibrary,
  Increase,
  ScenarioFlag,
  duplicateScenarioIcon,
  OutdatedIcon,
} from '../../../assets/images/UI-dashboard';
import ScenarioCardTables from './ScenarioCardTables';
import {
  fetchEditScenarioData,
  fetchUserData,
  openShareDialog,
  setDuplicateScenarioFlag,
  setEditScenarioFlag,
  updatedSelectedUserShareList,
} from '../../../features/scenarioLibrary/scenarioLibrarySlice';
import { useAppDispatch, useAppSelector } from '../../../state/hooks';
import { useNavigate } from 'react-router-dom';
import { toggleEnableReviewTableDialog } from '../../ScenarioCreation/components/ReviewTable/reviewTableSlice';
import { setScenarioAction, setScenarioName } from '../../../features/scenarioCreation/scenarioCreationSlice';
import { resetForm } from '../../../components/FormComponent/formSlice';
import { hideLoader, showLoader } from '../../../features/ui/loaderSlice';
import { SCENARIO_LIBRARY_DATA } from '../../../utils/dataConstants';
import { StatusMapperKeys } from '../../../types/common';
import { formatNumber } from '../../../utils/helper';
import RefreshDialog from '../../../components/UI/RefreshDialog';
import { RootState } from '../../../state/store';

interface ScenarioCardProps {
  data: any;
  selectedScenarios: string[];
  onSelectScenario: (id: string, selected: boolean) => void;
  scenariosForComparison: string[];
  handleDelete: (name: string, user: string) => void;
  handleUpateOrRefresh: (value: string, name: string) => void;
  editScenarios: (name: string) => void;
}

const ScenarioCards: React.FC<ScenarioCardProps> = ({
  data,
  selectedScenarios,
  onSelectScenario,
  scenariosForComparison,
  handleDelete,
  handleUpateOrRefresh,
  editScenarios,
}) => {
  const dispatch = useAppDispatch();
  const [openRefreshDialog, setOpenRefreshDialog] = useState<boolean>(false);
  const [updateOrRefresh, setUpdateOrRefresh] = useState({
    name: '',
  });
  const {
    scenario_name,
    created_at,
    user,
    scenario_id,
    input,
    txn_gr,
    vol_uc_gr,
    nr_gr,
    share_change,
    ou_gp,
    system_gp,
    status,
    scenario_expired,
  } = data;

  // const isChecked = selectedScenarios.includes(scenario_id);
  const { comingFromScreen } = useAppSelector((state: RootState) => state.scenarioLibrary);
  const isChecked = selectedScenarios.includes(input[0]['scenario_id']);

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // onSelectScenario(scenario_id, event.target.checked);
    onSelectScenario(input[0]['scenario_id'], event.target.checked);
  };

  const editScenario = async (name: string) => {
    editScenarios(name);
  };

  const duplicateScenario = (name: string) => {
    setUpdateOrRefresh({ name: name });
    setOpenRefreshDialog(true);
  };

  const handleConfirmUpdateOrRefresh = (value: string) => {
    handleUpateOrRefresh(value, updateOrRefresh.name);
    setOpenRefreshDialog(false);
  };

  const shareScenario = async (name: string) => {
    await dispatch(showLoader());
    await dispatch(setScenarioName(name));
    await dispatch(fetchUserData());
    await dispatch(dispatch(updatedSelectedUserShareList([])));
    await dispatch(hideLoader());
    await dispatch(openShareDialog());
  };

  const handleCloseRefreshDialog = () => {
    setOpenRefreshDialog(false);
  };

  const deleteScenario = () => {
    handleDelete(scenario_name, user);
  };
  return (
    <>
      <ScenarioCardContainer status={status} isChecked={isChecked}>
        <Card>
          <CardHeader
            title={
              <StyledHeaderContainer>
                <Grid>
                  <StyledCardHeaderTitle>
                    {scenario_name}
                    {scenario_expired && <StyledTagImage src={OutdatedIcon} alt="scenario-tag" />}
                  </StyledCardHeaderTitle>
                </Grid>
                <Grid
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  {(status === 'complete' || comingFromScreen === 'scenarioCreation') && (
                    <>
                      <StyledCheckbox
                        checked={isChecked}
                        onChange={(event) => {
                          handleCheckboxChange(event);
                        }}
                        color="primary"
                      />
                      <CompareText>Select</CompareText>
                    </>
                  )}
                  <StyledCardHeaderBox status={status}>
                    <StyledStatusText status={status}>
                      {SCENARIO_LIBRARY_DATA.STATUS_MAPPER[status as StatusMapperKeys]}
                    </StyledStatusText>
                  </StyledCardHeaderBox>
                </Grid>
              </StyledHeaderContainer>
            }
          />
          <StyledGridContainer>
            <StyledGridLeft>
              <img style={{ marginRight: '4px' }} src={UserIconLibrary} alt="edit" />
              <StyledUserName>{user}</StyledUserName>
              <StyledUserDate>| Date Created: {created_at}</StyledUserDate>
            </StyledGridLeft>
            <StyledGridRight>
              <Tooltip title={status === 'complete' ? 'Duplicate Scenario' : 'Edit Scenario'}>
                <StyledImage
                  src={status === 'complete' ? Update : Editable}
                  alt="edit"
                  onClick={
                    status === 'complete' ? () => duplicateScenario(scenario_name) : () => editScenario(scenario_name)
                  }
                />
              </Tooltip>

              <Tooltip title="Delete Scenario">
                <StyledImage src={Delete} alt="delete" onClick={deleteScenario} />
              </Tooltip>

              <Tooltip title={status === 'complete' ? 'Share Scenario' : 'Cannot Share'}>
                <StyledImage
                  src={status === 'complete' ? Shareable : NonShareable}
                  alt="share"
                  onClick={status === 'complete' ? () => shareScenario(scenario_name) : undefined}
                />
              </Tooltip>
            </StyledGridRight>
          </StyledGridContainer>
          <StyledCardContent>
            <Grid>
              <ScenarioCardTables dataRows={input} />
            </Grid>

            <StyledMetricBox>
              <Grid container spacing={0}>
                <StyledMetricGrid item xs={6}>
                  <StyledMetricTitle>Txn Gr %</StyledMetricTitle>
                  <StyledMetricTypography>
                    <img src={Increase} alt="increase" />
                    {status === 'complete' ? formatNumber(txn_gr) : '--'}%
                  </StyledMetricTypography>
                </StyledMetricGrid>
                <StyledNoBorderGrid item xs={6}>
                  <StyledMetricTitle>Vol UC Gr %</StyledMetricTitle>
                  <StyledMetricTypography>
                    <img src={Increase} alt="increase" />
                    {status === 'complete' ? formatNumber(vol_uc_gr) : '--'}%
                  </StyledMetricTypography>
                </StyledNoBorderGrid>
                <StyledMetricGrid item xs={6}>
                  <StyledMetricTitle>NR Gr %</StyledMetricTitle>
                  <StyledMetricTypography>
                    <img src={Increase} alt="increase" />
                    {status === 'complete' ? formatNumber(nr_gr) : '--'}%
                  </StyledMetricTypography>
                </StyledMetricGrid>
                <StyledNoBorderGrid item xs={6}>
                  <StyledMetricTitle>Share Ch %</StyledMetricTitle>
                  <StyledMetricTypography>
                    <img src={Increase} alt="increase" />
                    {status === 'complete' ? formatNumber(share_change) : '--'}%
                  </StyledMetricTypography>
                </StyledNoBorderGrid>
                <StyledMetricGrid style={{ borderBottom: 'none' }} item xs={6}>
                  <StyledMetricTitle>Ou GP %</StyledMetricTitle>
                  <StyledMetricTypography>
                    <img src={Increase} alt="increase" />
                    {status === 'complete' ? formatNumber(ou_gp) : '--'}%
                  </StyledMetricTypography>
                </StyledMetricGrid>
                <StyledNoBorderGrid item xs={6}>
                  <StyledMetricTitle>System GP %</StyledMetricTitle>
                  <StyledMetricTypography>
                    <img src={Increase} alt="increase" />
                    {status === 'complete' ? formatNumber(system_gp) : '--'}%
                  </StyledMetricTypography>
                </StyledNoBorderGrid>
              </Grid>
            </StyledMetricBox>
          </StyledCardContent>
        </Card>
      </ScenarioCardContainer>
      <RefreshDialog
        open={openRefreshDialog}
        onClose={handleCloseRefreshDialog}
        onConfirm={handleConfirmUpdateOrRefresh}
        value={scenario_expired}
      />
    </>
  );
};

export default ScenarioCards;
